// app/javascript/modules/ferias/form_funcionario.js
// Lógica do campo funcionário:
//   - Mostra/esconde o corpo do formulário
//   - Preload por ?funcionario_id na URL
//   - Painel informativo de férias já agendadas
//   - Validação no submit (impede salvar sem funcionário selecionado)
//   - Callback window.feriasOnFuncionarioSelected (chamado pelo autocomplete inline)

export function initFeriasFuncionario(form, { apiFeriasUrl, retornaFuncionarioUrl }) {
  const $j = window.$j
  if (typeof $j === 'undefined') return { destroy() {} }

  const autocompleteInput  = form.querySelector('#autocomplete_ferias_rm_funcionario_id')
  const hiddenComponent    = form.querySelector('#ferias_rm_funcionario_id')
  const hiddenModel        = form.querySelector('#movimentacoes_pessoal_rm_funcionario_id')
  const dataLimiteInput    = form.querySelector('#params_data_limite_inicio_ferias')
  const formBody           = form.querySelector('#ferias-form-body')
  const infoPanel          = form.querySelector('#ferias-funcionario-info')
  const submitBtn          = form.querySelector('#cria-objeto')
  const funcaoInfoEl       = form.querySelector('#ferias-funcionario-funcao-escala')
  const escalaTipoInput    = form.querySelector('#ferias_tipo_escala')

  const ESCALA_LABELS = { 1: '6x1', 2: '5x2', 3: '12x36', 4: '24x72' }

  function setFuncionarioId(id) {
    const val = (id || '').toString().trim()
    if (hiddenComponent) hiddenComponent.value = val
    if (hiddenModel)     hiddenModel.value      = val
  }

  function setFuncaoEscala(funcao, tipoEscala, horario) {
    if (escalaTipoInput) escalaTipoInput.value = tipoEscala || ''
    if (!funcaoInfoEl) return
    // Tenta label pelo tipo_escala; se vazio, usa o campo horario como fallback
    const escalaLabel = ESCALA_LABELS[parseInt(tipoEscala)] || (horario ? horario.toString().trim() : null)
    const parts = [funcao, escalaLabel].filter(Boolean)
    if (parts.length) {
      funcaoInfoEl.textContent = parts.join(' · ')
      funcaoInfoEl.classList.remove('hidden')
    } else {
      funcaoInfoEl.classList.add('hidden')
    }
  }

  function getFuncionarioId() {
    return hiddenModel?.value || hiddenComponent?.value || ''
  }

  function isoToBR(iso) {
    if (!iso) return ''
    // Accepts yyyy-mm-dd or yyyy/mm/dd
    const m = iso.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})/)
    if (m) return `${m[3]}/${m[2]}/${m[1]}`
    return iso
  }

  // ── Form body show/hide ───────────────────────────────────────────────────

  function showFormBody(show) {
    if (!formBody) return
    formBody.classList.toggle('hidden', !show)
  }

  function setSubmitEnabled(enabled) {
    if (!submitBtn) return
    submitBtn.disabled = !enabled
    submitBtn.classList.toggle('opacity-50',        !enabled)
    submitBtn.classList.toggle('cursor-not-allowed', !enabled)
  }

  // ── Vacation info panel ───────────────────────────────────────────────────

  function fetchFeriasAgendadas(id) {
    if (!infoPanel) return
    const url = formBody?.dataset.feriasAgendadasUrl
    if (!url) { infoPanel.classList.add('hidden'); infoPanel.innerHTML = ''; return }

    infoPanel.innerHTML = '<p class="text-xs text-gray-400 mt-2">Buscando férias agendadas...</p>'
    infoPanel.classList.remove('hidden')

    $j.ajax({
      url,
      data: { funcionario_id: id },
      dataType: 'json',
      success(data) {
        renderFeriasInfo(data.ferias || [], !!data.bloqueia_nova_ferias, data.termino_ultima_fracao)
      },
      error() {
        infoPanel.classList.add('hidden')
        infoPanel.innerHTML = ''
        showFormBody(true)
        setSubmitEnabled(true)
      },
    })
  }

  // Em modo edição o funcionário já está fixo — nunca bloquear o formulário
  // pois a férias existente é exatamente a que está sendo editada.
  const isEditMode = !!(autocompleteInput?.hasAttribute('readonly'))

  function renderFeriasInfo(ferias, bloqueia, terminoUltimaFracao) {
    if (isEditMode) bloqueia = false

    if (!ferias.length) {
      infoPanel?.classList.add('hidden')
      if (infoPanel) infoPanel.innerHTML = ''
      showFormBody(true)
      setSubmitEnabled(true)
      return
    }

    const borderCls  = bloqueia ? 'border-red-200'   : 'border-amber-200'
    const bgCls      = bloqueia ? 'bg-red-50'        : 'bg-amber-50'
    const hdrBgCls   = bloqueia ? 'bg-red-100'       : 'bg-amber-100'
    const hdrBdrCls  = bloqueia ? 'border-red-200'   : 'border-amber-200'
    const iconColor  = bloqueia ? 'text-red-700'     : 'text-amber-700'
    const titleColor = bloqueia ? 'text-red-800'     : 'text-amber-800'
    const divColor   = bloqueia ? 'divide-red-100'   : 'divide-amber-100'
    const lblColor   = bloqueia ? 'text-red-600'     : 'text-amber-600'
    const valColor   = bloqueia ? 'text-red-900'     : 'text-amber-900'

    const hasMultiple = ferias.some(f => f.total_fracoes > 1)
    const titleText   = hasMultiple
      ? 'Férias fracionadas já agendadas para este funcionário'
      : 'Férias já agendadas para este funcionário'

    let html = `<div class="mt-3 rounded-md border ${borderCls} ${bgCls} overflow-hidden">`

    // Header
    const iconSvg = bloqueia
      ? '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3.5 h-3.5"><path fill-rule="evenodd" d="M6.701 2.25c.577-1 2.026-1 2.598 0l5.196 9a1.5 1.5 0 0 1-1.299 2.25H2.804a1.5 1.5 0 0 1-1.3-2.25l5.197-9ZM8 6a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-1.5 0v-2.5A.75.75 0 0 1 8 6Zm0 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3.5 h-3.5"><path fill-rule="evenodd" d="M8 1a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 8 1ZM10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM12.9 4.16a.75.75 0 0 0-1.06-1.06l-1.097 1.097a.75.75 0 0 0 1.06 1.06L12.9 4.16ZM15 8a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 15 8ZM11.843 11.843a.75.75 0 0 0 1.06-1.061l-1.097-1.096a.75.75 0 0 0-1.06 1.06l1.097 1.097ZM8 12.25a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0V13a.75.75 0 0 1 .75-.75ZM4.16 11.843a.75.75 0 0 0-1.061-1.06l-1.097 1.096a.75.75 0 0 0 1.061 1.061l1.097-1.097ZM1 8a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 0 1.5h-1.5A.75.75 0 0 1 1 8ZM4.157 4.16 3.06 3.063A.75.75 0 0 0 2 4.124l1.097 1.096a.75.75 0 0 0 1.06-1.06Z" clip-rule="evenodd"/></svg>'

    html += `<div class="px-3 py-2 border-b ${hdrBdrCls} ${hdrBgCls} flex items-center gap-2">`
    html += `<span class="${iconColor}">${iconSvg}</span>`
    html += `<span class="text-xs font-semibold ${titleColor}">${titleText}</span>`
    html += '</div>'

    // Periods
    html += `<div class="divide-y ${divColor}">`
    ferias.forEach(f => {
      const isNow = !!f.em_ferias
      let badgeCls, badgeLabel
      if (isNow) {
        badgeCls = 'bg-green-100 text-green-800 border-green-300'; badgeLabel = 'Em férias agora'
      } else if (f.status === 'Aprovada') {
        badgeCls = 'bg-blue-100 text-blue-800 border-blue-200'; badgeLabel = 'Aprovada'
      } else {
        badgeCls = 'bg-yellow-100 text-yellow-800 border-yellow-200'; badgeLabel = 'Pendente de aprovação'
      }
      const fracaoLabel = (f.fracao_indice && f.total_fracoes > 1)
        ? ` — Fração ${f.fracao_indice}/${f.total_fracoes}` : ''

      html += '<div class="px-3 py-2.5 grid grid-cols-3 gap-x-4 gap-y-1">'
      html += '<div class="col-span-3 mb-1 flex items-center gap-2">'
      html += `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${badgeCls}">${badgeLabel}</span>`
      if (fracaoLabel) html += `<span class="text-xs text-gray-500 font-medium">${fracaoLabel}</span>`
      html += '</div>'
      html += `<div><p class="text-xs ${lblColor} font-medium">Início do gozo</p><p class="text-sm ${valColor} font-semibold">${f.inicio || '—'}</p></div>`
      html += `<div><p class="text-xs ${lblColor} font-medium">Fim do gozo</p><p class="text-sm ${valColor} font-semibold">${f.termino || '—'}</p></div>`
      html += `<div><p class="text-xs ${lblColor} font-medium">Dias</p><p class="text-sm ${valColor} font-semibold">${f.dias != null ? f.dias + ' dias' : '—'}</p></div>`
      html += `<div class="col-span-3 mt-1"><p class="text-xs ${lblColor} font-medium">Data de pagamento</p><p class="text-sm ${valColor} font-semibold">${f.data_pagamento || '—'}</p></div>`
      html += '</div>'
    })
    html += '</div>'

    if (bloqueia) {
      const terminoTexto = terminoUltimaFracao ? ` até <strong>${terminoUltimaFracao}</strong>` : ''
      html += '<div class="px-3 py-2.5 bg-red-100 border-t border-red-200">'
      html += `<p class="text-xs text-red-800 leading-snug"><strong>Cadastro bloqueado:</strong> este funcionário possui férias agendadas${terminoTexto}. Novas férias só poderão ser cadastradas após a conclusão do período atual ou o cancelamento das férias agendadas.</p>`
      html += '</div>'
    }

    html += '</div>'

    if (infoPanel) {
      infoPanel.innerHTML = html
      infoPanel.classList.remove('hidden')
    }

    showFormBody(!bloqueia)
    setSubmitEnabled(!bloqueia)
  }

  // ── Preload via ?funcionario_id na URL ────────────────────────────────────

  const urlParams          = new URLSearchParams(window.location.search)
  const funcionarioIdParam = urlParams.get('funcionario_id')

  if (funcionarioIdParam && autocompleteInput) {
    $j.ajax({
      url: retornaFuncionarioUrl,
      method: 'GET',
      data: { funcionario_id: funcionarioIdParam },
      success(data) {
        if (data?.results?.length > 0) {
          $j(autocompleteInput).val(data.results[0][0]).attr('disabled', true)
          setFuncionarioId(funcionarioIdParam)
          setFuncaoEscala(data.results[0][2], data.results[0][3], data.results[0][4])
          fetchFeriasAgendadas(funcionarioIdParam)
        }
      },
    })

    $j.ajax({
      url: apiFeriasUrl,
      method: 'GET',
      data: { funcionario_id: funcionarioIdParam },
      success(data) {
        if (data?.data_limite_inicio_ferias && dataLimiteInput) {
          dataLimiteInput.value = isoToBR(data.data_limite_inicio_ferias)
        }
      },
    })
  }

  // ── Callback do autocomplete inline ──────────────────────────────────────
  // O autocomplete jQuery UI do _form.html.erb chama este callback após selecionar.

  window.feriasOnFuncionarioSelected = function(id, _nome, funcao, tipoEscala, horario) {
    setFuncaoEscala(funcao, tipoEscala, horario)
    fetchFeriasAgendadas(id)
  }

  // Se em modo edição (funcionário já selecionado), busca férias e exibe corpo
  const existingId = getFuncionarioId()
  if (existingId) {
    showFormBody(true)
    fetchFeriasAgendadas(existingId)
  }

  // ── Validação no submit ───────────────────────────────────────────────────

  function handleSubmit(e) {
    if (!getFuncionarioId()) {
      e.preventDefault()
      e.stopImmediatePropagation()
      if (autocompleteInput) {
        autocompleteInput.focus()
        autocompleteInput.classList.add('border-red-400')
        autocompleteInput.classList.remove('border-gray-300')
        const errEl = form.querySelector('#erro-funcionario')
        if (errEl) {
          errEl.textContent = 'Selecione um funcionário para continuar.'
          errEl.classList.remove('hidden')
        }
      }
    }
  }

  submitBtn?.addEventListener('click', handleSubmit)

  return {
    destroy() {
      submitBtn?.removeEventListener('click', handleSubmit)
      delete window.feriasOnFuncionarioSelected
    },
  }
}
